import React, { useMemo } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import config from 'config';

import FeedbackModalWrapper from 'components/FeedbackModalWrapper/FeedbackModalWrapper';
import { Player } from 'components/CamPlayerKbygRewinds/usePlayerState/usePlayerState';

import { RefState } from 'hooks/useRefState';

import { rewindSharePath } from 'utils/urls/rewindSharePath';
import { highlightsSharePath } from 'utils/urls/highlightsShareUrl';
import { liveCamSharePath } from 'utils/urls/liveCamSharePath';

import ShareForm from './ShareForm';

interface Props {
  spotName: string;
  spotId: string;
  camId: string;
  handleCloseClick: () => void;
  currentPlayer?: Player;
  currentTimestampRef: RefState<Date | undefined>;
  spotTimezone: string;
  isRewindPlayer: boolean;
  onShareUrlChange: (url: string) => void;
  cameraTitle: string;
  clipIndex?: number;
}

const ShareFormWrapper = ({
  handleCloseClick,
  spotName,
  spotId,
  camId,
  currentPlayer,
  currentTimestampRef,
  spotTimezone,
  isRewindPlayer,
  clipIndex,
  onShareUrlChange,
  cameraTitle,
}: Props) => {
  const rewindShareUrl = useMemo(
    () =>
      currentTimestampRef.current && currentTimestampRef.current.value
        ? `${config.appHost}${rewindSharePath({
            spotName,
            spotId,
            currentTimestamp: currentTimestampRef.current.value.getTime().toString(),
            camId,
          })}`
        : '',
    [spotName, spotId, camId, currentTimestampRef.current],
  );
  const liveCamShareUrl = useMemo(
    () => `${config.appHost}${liveCamSharePath({ spotName, spotId, camId })}`,
    [spotName, spotId, camId],
  );

  const highlightsShareUrl = useMemo(
    () =>
      `${config.appHost}${highlightsSharePath({
        spotName,
        spotId,
        camId,
        clipIndex,
      })}`,
    [spotName, spotId, camId, clipIndex],
  );

  const formattedTime = useMemo(
    () =>
      currentTimestampRef.current && currentTimestampRef.current.value
        ? formatInTimeZone(currentTimestampRef.current.value, spotTimezone, 'h:mm:ss aaa')
        : '',
    [currentTimestampRef.current, spotTimezone],
  );

  return (
    <FeedbackModalWrapper
      handleCloseClick={handleCloseClick}
      title={spotName}
      subTitle="Share Cam"
      showTitle
      dataTestId="share-modal"
    >
      <ShareForm
        formattedTime={formattedTime}
        rewindShareUrl={rewindShareUrl}
        liveCamShareUrl={liveCamShareUrl}
        highlightsShareUrl={highlightsShareUrl}
        isRewindPlayer={isRewindPlayer}
        currentPlayer={currentPlayer}
        clipIndex={clipIndex}
        onShareUrlChange={onShareUrlChange}
        cameraTitle={cameraTitle}
        spotId={spotId}
        cameraId={camId}
        spotName={spotName}
      />
    </FeedbackModalWrapper>
  );
};

export default ShareFormWrapper;
